import { useTranslation } from "@next-platform/client-common-core";
import { useRecommendedPlanName } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Button, ButtonSize, ButtonVariant, IconVariants, Image, Modal, ModalSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useMemo } from "react";

import { PlanLinks, SiteLogo } from "@/components";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, ROUTES } from "@/constants";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl } from "@/utils";

import { ModalPremiumAccessProps } from "./types";

const TEXT_COLOR = "text-gray-12";

export default function ModalPremiumAccess(props: ModalPremiumAccessProps) {
  const { t } = useTranslation();
  const applicationId = basicAppConfig.applicationId;
  const { isLoading, isOpen, onClose, characterAvatarUrl, isCharacterAvatarBlurred, paywallRoles } = props;
  const conversationImagesDocUrl =
    EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.conversation_images_doc;

  const planNames = useRecommendedPlanName(paywallRoles ?? []);
  const moreDetailsValues = useMemo(() => ({ linkText: t("chat:modal.generateImage.upgradePlan.linkText") }), [t]);
  const moreDetailsComponents = useMemo(
    () => ({
      linkText: <LocalizedLink to={conversationImagesDocUrl} target="_blank" className="underline" />,
    }),
    [conversationImagesDocUrl],
  );

  return (
    <Modal
      title={t("chat:modal.generateImage.upgradePlan.title")}
      className="dark:!bg-gray-6 gap-md"
      size={ModalSize.LARGE}
      open={isOpen}
      onOpenChange={onClose}
      loading={isLoading}
      data-testid="generateImageUpgradeModal"
    >
      <Image
        src={getAvatarUrl(characterAvatarUrl, { preset: AvatarPreset.IMAGE_500X650 }) as string}
        alt={t("chat:modal.generateImage.upgradePlan.imageAlt")}
        className={cn("mx-auto h-auto w-auto max-h-[400px] max-w-full rounded-[20px] !object-contain", isCharacterAvatarBlurred && "blur-md")}
        data-testid="generateImageUpgradeModal-Image"
      />
      <Text variant={TextVariants.LABEL_LARGE_REGULAR} className={TEXT_COLOR}>
        <Trans i18nKey="chat:modal.generateImage.upgradePlan.description" />
      </Text>
      <Text variant={TextVariants.LABEL_SMALL_REGULAR} className={TEXT_COLOR}>
        {planNames.length ? (
          <>
            <Trans i18nKey="chat:modal.generateImage.upgradePlan.availableOnPlans" /> <PlanLinks planNames={planNames} />
            <Trans i18nKey="chat:modal.upgradePlan.plans" />
          </>
        ) : null}
      </Text>
      <LocalizedLink to={ROUTES.subscribe}>
        <Button
          iconStart={IconVariants.CROWN}
          size={ButtonSize.LARGE}
          variant={ButtonVariant.TRANSPARENT}
          className="w-full gap-lg bg-gradient-to-r from-purple-9 to-blue-9 text-white"
        >
          <Trans i18nKey="chat:modal.upgradePlan.cta.upgrade" />
        </Button>
      </LocalizedLink>
      <div className="flex flex-wrap gap-md">
        <Text variant={TextVariants.LABEL_XSMALL_REGULAR} className={TEXT_COLOR}>
          <Trans i18nKey="chat:modal.upgradePlan.moreDetails" values={moreDetailsValues} components={moreDetailsComponents} />
        </Text>
        <SiteLogo type="icon" className="size-4" />
      </div>
    </Modal>
  );
}
